import { MemorySettingsBackend, parseSettings } from "@cuekards/core";
import type { Settings } from "@cuekards/core";
import {
  messageKind,
  type MessagePortLike,
  type SettingsSnapshot,
  type SettingsWrite,
} from "./protocol.js";

/**
 * Where settings live in a browser, and why they do not live where you would
 * expect.
 *
 * The settings store's accessors are synchronous, and they have to be: they are
 * read from inside controllers that have no `await` to give, and making them
 * asynchronous would ripple through every one of them. The store lives in the
 * core worker, with the controllers. But the browser's only synchronous storage
 * is `localStorage`, which is window-scoped and therefore unreachable from a
 * worker at all — and the storage that *is* reachable from one is asynchronous.
 *
 * So the page owns persistence and the worker owns the store. The page reads the
 * blob at startup and posts it in; the worker caches that snapshot and answers
 * every read from the cache; each write posts back out for the page to store.
 * One message in each direction, and the interface stays synchronous.
 *
 * Settings are consequently per browser profile, not per person. The two
 * mitigations that need no server are web ticket 18's and both hang off this
 * arrangement: a settings *file* is written from the page's stored blob and
 * imported by posting a fresh snapshot, and a rig in the *URL* is a
 * {@link SettingsOverride} applied to the snapshot on the way in — see
 * `page/settings-file.ts` and `page/settings-url.ts`.
 */

/** The one key the blob is stored under. Namespaced, because a page shares
 * origin storage with everything else the origin ever ships. */
export const SETTINGS_STORAGE_KEY = "cuekards.settings";

/** The bit of `localStorage` this needs. Narrow so it can be faked, and so the
 * page half stays testable without a browser. */
export interface SettingsStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * This browser's own storage, or nothing where there is none to be had.
 *
 * Reaching for `window.localStorage` is itself allowed to throw — a tab with
 * site data blocked does exactly that, on the property access rather than on the
 * call — so the one place the platform is touched is a function that cannot. The
 * page passes what comes back straight to {@link attachSettingsPersistence},
 * whose two readers already treat "no storage" as "nothing stored": cuekards runs,
 * and the settings are this session's only. The settings screen says so, which
 * is the whole of story 61 — the surprise is what makes losing a setup a support
 * problem, not the loss.
 */
export function thisBrowsersStorage(): SettingsStorage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** Nowhere to put anything. What a tab with site data blocked gets, so that the
 * absence is answered once, here, rather than by every caller guarding. */
const NO_STORAGE: SettingsStorage = {
  getItem: () => null,
  setItem: () => undefined,
};

/**
 * The storage to persist settings through: this browser's, or one that forgets.
 *
 * Distinct from {@link thisBrowsersStorage}, which still answers "is there any",
 * because one caller needs to know: the first-run walkthrough is *not* shown to
 * a browser that cannot remember having shown it, and a storage that silently
 * forgets would put it on the screen every single visit.
 */
export function settingsStorage(): SettingsStorage {
  return thisBrowsersStorage() ?? NO_STORAGE;
}

/**
 * The stored settings, or empty settings when there are none readable.
 *
 * Never throws, for the same reason the desktop's file backend does not: a store
 * that cannot be read must not be able to stop the app starting. That covers a
 * corrupt blob — `parseSettings` is the shared rule for those — and the access
 * itself failing, which is what a tab with site data blocked does.
 */
export function readStoredSettings(storage: SettingsStorage): Settings {
  try {
    return parseSettings(storage.getItem(SETTINGS_STORAGE_KEY));
  } catch {
    return {};
  }
}

/**
 * Persist the blob. Never throws either: storage can refuse a write for being
 * over quota or blocked outright, and losing a font size must not take down
 * whatever the user was doing when the write happened — which may well be
 * recording.
 */
export function writeStoredSettings(
  storage: SettingsStorage,
  settings: Settings,
): void {
  try {
    storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Nothing useful to do here: the settings are still live in the worker for
    // this session, and only this session.
  }
}

/**
 * The core worker's backend: an in-memory cache of the page's snapshot, which
 * posts each write back out.
 *
 * A {@link MemorySettingsBackend} is exactly the caching half, including its
 * copy-in-and-out, so this adds the two things that are its own — where the
 * first snapshot comes from, and where a write goes.
 */
export class PageBackedSettingsBackend extends MemorySettingsBackend {
  readonly #persist: (settings: Settings) => void;

  constructor(persist: (settings: Settings) => void) {
    super();
    this.#persist = persist;
  }

  override write(settings: Settings): void {
    super.write(settings);
    // Read back rather than forwarding the argument, so what the page stores is
    // the copy the cache holds and not a reference the caller can still mutate.
    this.#persist(this.read());
  }

  /**
   * Take the page's snapshot as the current settings, without persisting it.
   *
   * Distinct from {@link write} on purpose: adopting is the page telling the
   * worker what is *already* stored, so writing it back would bounce it straight
   * out again on every start.
   */
  adopt(settings: Settings): void {
    super.write(settings);
  }
}

/**
 * The worker end of the arrangement: a backend that persists through the port,
 * seeded by the snapshot the page posts.
 *
 * Ordering is what makes the seeding safe, and it comes free. A worker's
 * messages are queued until its script has finished evaluating, and a port
 * delivers in order — so as long as this is attached at module scope and the
 * page posts the snapshot first, it is adopted before any request that reads a
 * setting is handled.
 *
 * **`onAdopted` is for the state that is decided once rather than read on
 * demand** (flow ticket 06's scroll mode). That ordering guarantee is about
 * *handled requests*, and a host that reads a setting synchronously while it is
 * still building its controllers is asking earlier than that — the snapshot is a
 * message, and nothing has been delivered yet. So the few decisions that have to
 * be made from a stored value, rather than re-read out of one, are made here.
 * Called once, immediately after the snapshot is adopted and before anything
 * else on the port is answered.
 */
export function attachSettingsBackend(
  port: MessagePortLike,
  onAdopted?: () => void,
): PageBackedSettingsBackend {
  const backend = new PageBackedSettingsBackend((settings) => {
    port.postMessage({ kind: "settings-write", settings } satisfies SettingsWrite);
  });
  port.addEventListener("message", (event) => {
    if (messageKind(event.data) !== "settings-snapshot") return;
    backend.adopt((event.data as SettingsSnapshot).settings);
    onAdopted?.();
  });
  port.start?.();
  return backend;
}

/**
 * Settings that are in force for this session without being this browser's.
 *
 * There is exactly one of these and it is the rig in the page URL (web ticket
 * 18, story 60), but the shape is deliberately ignorant of URLs: this module
 * knows the *rule* — some settings can be in force without being saved — and
 * `page/settings-url.ts` knows what a `?font=64` means.
 *
 * Two methods rather than one, because the two directions are genuinely
 * different questions and answering only the first is the bug. Seeding alone
 * would put the URL's values in front of the user; it would also let the first
 * write of anything at all flush them into storage along with it, which is a
 * shared link quietly rewriting the recipient's saved setup.
 */
export interface SettingsOverride {
  /** The settings this session runs on, given what this browser has stored. */
  seed(stored: Settings): Settings;
  /** What to store, given what the worker wrote and what was stored before. */
  keep(written: Settings, stored: Settings): Settings;
}

/**
 * The page end: post what is stored, then store what comes back.
 *
 * Called before anything else is sent down the port, so the snapshot is the
 * first message the worker sees.
 *
 * Storage is re-read on every write rather than remembered, because it — and not
 * anything held here — is what the next page load will start from.
 */
export function attachSettingsPersistence(
  port: MessagePortLike,
  storage: SettingsStorage,
  override?: SettingsOverride,
): void {
  port.addEventListener("message", (event) => {
    if (messageKind(event.data) !== "settings-write") return;
    const written = (event.data as SettingsWrite).settings;
    writeStoredSettings(
      storage,
      override === undefined
        ? written
        : override.keep(written, readStoredSettings(storage)),
    );
  });
  port.start?.();

  /**
   * The snapshot, posted once and only once.
   *
   * A second one would be adopted — the store answers every read through the
   * backend rather than caching — but it would tell no controller, and each of
   * them read what it needed when it was constructed. So replacing a whole
   * settings blob is a page load rather than a message; see
   * `page/SettingsTransfer.tsx`, where importing a file does exactly that.
   */
  const stored = readStoredSettings(storage);
  port.postMessage({
    kind: "settings-snapshot",
    settings: override === undefined ? stored : override.seed(stored),
  } satisfies SettingsSnapshot);
}
